import { ReactElement } from 'react';
import { flexRender } from '@tanstack/react-table';

import type { Header } from '@tanstack/react-table';

import { cn } from 'support/helpers/cn/cn';
import { WithRef } from 'support/types/withRef';

import { getBaseBorderClasses, getHeaderBorderClasses } from '../../utils/borderUtils';
import { getPinningStyles } from '../../layoutHelpers/getPinningStyles';

type Props<TData> = {
  header: Header<TData, unknown>;
  betweenCellBorders: boolean;
  isFirstColumn: boolean;
  isLastColumn: boolean;
  isAction: boolean;
  className?: string;
  style?: React.CSSProperties;
};

const ThComponent = <TData = unknown,>({
  style,
  header,
  isFirstColumn,
  isLastColumn,
  className,
  betweenCellBorders,
  isAction,
  ref,
}: WithRef<Props<TData>, HTMLTableCellElement>) => {
  const borderClasses = getHeaderBorderClasses({
    isFirstColumn,
    isLastColumn,
    betweenCellBorders,
    isAction,
  });

  const isGroupColumn = header.column.columns.length > 0;
  const subHeaders = header.column.getLeafColumns();
  const isSecondRowOfSingleHeader = header.column.depth === 0 && header.depth > 1;

  // Return null for headers when header.column.depth === 0 and header.depth > 1.
  // This condition occurs when rendering header rows beyond the first (i.e., header.depth > 1),
  // and the column is a top-level column (depth === 0). In these cases, the header cell would
  // otherwise be rendered multiple times for subheader rows, leading to duplicate header cells.
  // For example, in a table with grouped columns, the first row renders group headers, and
  // subsequent rows render subheaders. Top-level columns should only appear in the first row,
  // so we return null for them in deeper header rows to avoid duplication.
  if (isSecondRowOfSingleHeader) {
    return null;
  }

  let displayContent = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
  let rowSpan = 1;

  //Subheaders have length 1 when there are grouped columns, but we are looking into a single column group
  const isSingleHeader = subHeaders.length === 1 && !isGroupColumn;
  if (isSingleHeader) {
    const subHeader = subHeaders[0];
    displayContent = flexRender(subHeader.columnDef.header, header.getContext());
    rowSpan = 2;
  }

  return (
    <th
      ref={ref}
      key={header.id}
      className={cn(
        'bg-white p-0 text-left text-xs font-normal whitespace-nowrap text-gray-500',
        getBaseBorderClasses(),
        { 'text-center': isGroupColumn },
        borderClasses,
        className,
      )}
      colSpan={header.colSpan}
      rowSpan={rowSpan}
      style={{
        ...style,
        ...getPinningStyles(header.column),
      }}
    >
      {displayContent}
    </th>
  );
};

export const Th = ThComponent as <TData = unknown>(props: WithRef<Props<TData>, HTMLTableCellElement>) => ReactElement;
